/**
 * The board's telemetry wall: eight small instruments under the fly, each one a
 * reading of the brain or of the chain it lives on, each one blinking when its own
 * event lands - a thought, a feed, a block. Nothing here animates by itself: a
 * still panel means a quiet chain.
 *
 *   readout    the four readout potentials a thought reports (p0..p3) against the
 *              threshold the kernel fires at
 *   raster     which of the 256 sensory cells fired, thought by thought
 *   senses     the 60 channels a feed can charge (n % 60), heated by the feed log
 *   heartbeat  Arc's own blocks as they arrive, with their transaction counts
 *   dossier    the newest thought's fired cells, one at a time, from the BANC tables
 *   census     the biggest cell classes, lit when the newest thought fired one
 *   sign       the etched synapses' weights: excitatory against inhibitory
 *   scope      spikes and synapses per thought, as two traces with their stats
 */

import { isThought, type Block, type Feed, type Tick } from "./api";
import type { Connectome, NeuronGeometry } from "./geometry";
import { t } from "./i18n";

const fmt = (n: number): string => n.toLocaleString("en-US");

const SENSORY = 256;

const CHANNELS = 60;

const CENSUS_ROWS = 8;

const el = (id: string): HTMLElement | null => document.getElementById(id);

function setText(id: string, text: string): void {
  const node = el(id);

  if (node !== null && node.textContent !== text) node.textContent = text;
}

/** one beat of a panel's live light: restart its CSS animation */
function blink(id: string): void {
  const node = el(id);

  if (node === null) return;

  node.classList.remove("is-blink");
  void node.offsetWidth;
  node.classList.add("is-blink");
}

const humanize = (name: string): string => name.replaceAll("_", " ");

export class Telemetry {
  readonly #geometry: NeuronGeometry;
  readonly #outDegree: Uint16Array;
  readonly #inDegree: Uint16Array;
  readonly #census: { index: number; count: number }[];
  #ticks: Tick[] = [];
  #feeds: Feed[] = [];
  #blocks: Block[] = [];
  #threshold = 12_800;
  #dossier: readonly number[] = [];
  #dossierAt = 0;
  #dossierTimer = 0;

  constructor(geometry: NeuronGeometry, connectome: Connectome) {
    this.#geometry = geometry;
    const slots = geometry.regions.length;

    this.#outDegree = new Uint16Array(slots);
    this.#inDegree = new Uint16Array(slots);

    for (let e = 0; e < connectome.sources.length; e += 1) {
      this.#outDegree[connectome.sources[e]] += 1;
      this.#inDegree[connectome.targets[e]] += 1;
    }

    const classCounts = new Map<number, number>();

    for (let i = 0; i < slots; i += 1) {
      const c = geometry.cellClasses[i];

      classCounts.set(c, (classCounts.get(c) ?? 0) + 1);
    }

    const unknown = geometry.meta.cellClasses.indexOf("unknown");

    this.#census = [...classCounts.entries()]
      .flatMap(([index, count]) => (index === unknown ? [] : [{ index, count }]))
      .sort((a, b) => b.count - a.count)
      .slice(0, CENSUS_ROWS);

    this.#paintSign(connectome);
    this.#paintCensus(null);
    this.#buildSenses();
  }

  setThreshold(threshold: number): void {
    if (threshold > 0) this.#threshold = threshold;
  }

  setHistory(ticks: readonly Tick[], feeds: readonly Feed[]): void {
    this.#ticks = ticks.filter(isThought);
    this.#feeds = [...feeds];
    this.#paintThoughts(false);
    this.#paintSenses(null);
  }

  thought(tick: Tick): void {
    if (!isThought(tick)) return;

    this.#ticks = [...this.#ticks, tick].slice(-400);
    this.#paintThoughts(true);
  }

  feed(feed: Feed): void {
    this.#feeds = [feed, ...this.#feeds].slice(0, 400);
    this.#paintSenses(feed.channel);
    blink("tlSenses");
  }

  block(block: Block): void {
    this.#blocks = [...this.#blocks, block].slice(-48);
    this.#paintHeart();
    blink("tlHeart");
  }

  relabel(): void {
    this.#paintThoughts(false);
    this.#paintSenses(null);
    this.#paintHeart();
    this.#paintDossier();
  }

  // ---- readout + raster + scope + dossier + census: one thought feeds them all ----
  #paintThoughts(live: boolean): void {
    const newest = this.#ticks.at(-1);

    this.#paintReadout(newest);
    this.#paintRaster();
    this.#paintScope();
    this.#paintCensus(newest ?? null);

    this.#dossier = newest?.fired ?? [];
    this.#dossierAt = 0;
    this.#paintDossier();
    window.clearInterval(this.#dossierTimer);

    if (this.#dossier.length > 1) {
      this.#dossierTimer = window.setInterval(() => {
        this.#dossierAt = (this.#dossierAt + 1) % this.#dossier.length;
        this.#paintDossier();
        blink("tlNeuron");
      }, 2_600);
    }

    if (live) {
      for (const id of ["tlReadout", "tlRaster", "tlScope", "tlNeuron", "tlCensus"]) blink(id);
    }
  }

  #paintReadout(tick: Tick | undefined): void {
    const threshold = this.#threshold;

    setText("tlReadoutS", t("tl.readoutS", { n: fmt(threshold) }));

    for (let i = 0; i < 4; i += 1) {
      const value = tick?.readout[i];
      const gauge = el(`tlGauge${i}`);

      setText(`tlP${i}`, value === undefined ? "—" : fmt(value));

      if (gauge !== null) {
        const ratio = value === undefined ? 0 : Math.max(0, Math.min(1, value / threshold));

        gauge.style.setProperty("--fill", `${(ratio * 100).toFixed(1)}%`);
        gauge.classList.toggle("is-near", ratio > 0.9);
      }
    }
  }

  #paintRaster(): void {
    const canvas = el("tlRasterCanvas");

    if (!(canvas instanceof HTMLCanvasElement)) return;

    const ctx = canvas.getContext("2d");

    if (ctx === null) return;

    const ticks = this.#ticks.slice(-96);
    const w = canvas.width;
    const h = canvas.height;
    const col = w / Math.max(1, ticks.length);

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "rgb(29 26 23 / 0.05)";

    for (let y = 0; y < SENSORY; y += 60) ctx.fillRect(0, (y / SENSORY) * h, w, 1);

    ticks.forEach((tick, i) => {
      const newest = i === ticks.length - 1;

      ctx.fillStyle = newest ? "#ae6200" : "#1d1a17";

      for (const cell of tick.fired) {
        if (cell >= SENSORY) continue;

        ctx.fillRect(i * col, (cell / SENSORY) * h, Math.max(1.5, col - 1), newest ? 3 : 2);
      }
    });

    const cells = new Set(ticks.flatMap((tick) => tick.fired.filter((c) => c < SENSORY)));

    setText("tlRasterS", t("tl.rasterS", { n: fmt(ticks.length), c: fmt(cells.size) }));
  }

  #paintScope(): void {
    const ticks = this.#ticks.slice(-60);
    const svg = el("tlScopeSvg");

    if (svg === null || ticks.length === 0) return;

    const spikes = ticks.map((x) => x.spiked);
    const synapses = ticks.map((x) => x.synapses);
    const max = Math.max(1, ...synapses, ...spikes);

    const line = (values: number[]): string =>
      values
        .map(
          (v, i) =>
            `${((i / Math.max(1, values.length - 1)) * 300).toFixed(1)},${(58 - (v / max) * 52).toFixed(1)}`,
        )
        .join(" ");

    svg.innerHTML =
      `<polyline class="syn" points="${line(synapses)}"/>` +
      `<polyline class="spk" points="${line(spikes)}"/>`;

    const mean = spikes.reduce((a, b) => a + b, 0) / spikes.length;
    const totalSyn = synapses.reduce((a, b) => a + b, 0);
    const totalSpk = spikes.reduce((a, b) => a + b, 0);

    setText("tlMean", mean.toFixed(1));
    setText("tlPeak", fmt(Math.max(...spikes)));
    setText("tlRatio", totalSpk === 0 ? "—" : (totalSyn / totalSpk).toFixed(2));
  }

  #paintDossier(): void {
    const cell = this.#dossier[this.#dossierAt];
    const g = this.#geometry;

    if (cell === undefined || cell >= g.regions.length) {
      for (const id of ["tlNId", "tlNType", "tlNClass", "tlNSuper", "tlNWhere", "tlNDeg"])
        setText(id, "—");

      return;
    }

    const meta = g.meta;

    setText("tlNCount", `${this.#dossierAt + 1} / ${this.#dossier.length}`);
    setText("tlNId", g.ids[cell]?.toString() ?? "—");
    setText("tlNSlot", `#${cell}`);
    setText("tlNType", meta.cellTypes[g.cellTypes[cell]] ?? "—");
    setText("tlNClass", humanize(meta.cellClasses[g.cellClasses[cell]] ?? "—"));
    setText("tlNSuper", humanize(meta.superClasses[g.superClasses[cell]] ?? "—"));
    setText(
      "tlNWhere",
      `${humanize(meta.regions[g.regions[cell]] ?? "—")} · ${meta.sides[g.sides[cell]] ?? "—"}`,
    );
    setText("tlNDeg", `${fmt(this.#outDegree[cell])} → · ← ${fmt(this.#inDegree[cell])}`);
    setText("tlNChannel", t("tl.channel", { n: String(cell % CHANNELS) }));
  }

  #paintCensus(newest: Tick | null): void {
    const host = el("tlCensusRows");

    if (host === null) return;

    const g = this.#geometry;
    const fired = new Set((newest?.fired ?? []).map((cell) => g.cellClasses[cell]));
    const max = this.#census[0]?.count ?? 1;

    host.innerHTML = this.#census
      .map(({ index, count }) => {
        const name = humanize(g.meta.cellClasses[index] ?? "");

        return `<li class="${fired.has(index) ? "is-lit" : ""}"><b>${name}</b><i style="--w:${((count / max) * 100).toFixed(1)}%"></i><span>${fmt(count)}</span></li>`;
      })
      .join("");
  }

  #paintSign(connectome: Connectome): void {
    let excitatory = 0;
    let inhibitory = 0;
    let magnitude = 0;
    const bins = Array.from({ length: 16 }, () => 0);
    const weights = connectome.weights;

    for (let e = 0; e < weights.length; e += 1) {
      const w = weights[e];

      if (w >= 0) excitatory += 1;
      else inhibitory += 1;

      magnitude += Math.abs(w);

      const b = Math.max(
        0,
        Math.min(15, 8 + Math.sign(w) * Math.min(7, Math.floor(Math.log2(Math.abs(w) + 1) / 2))),
      );

      bins[b] += 1;
    }

    const total = Math.max(1, excitatory + inhibitory);

    setText("tlExc", fmt(excitatory));
    setText("tlInh", fmt(inhibitory));
    setText("tlMag", (magnitude / total).toFixed(1));
    el("tlSignBar")?.style.setProperty("--exc", `${((excitatory / total) * 100).toFixed(1)}%`);

    const svg = el("tlSignHist");

    if (svg !== null) {
      const peak = Math.max(1, ...bins.map((n) => Math.log1p(n)));

      svg.innerHTML = bins
        .map((n, i) => {
          const h = (Math.log1p(n) / peak) * 30;

          return `<rect class="${i < 8 ? "inh" : "exc"}" x="${i * 7.5}" y="${32 - h}" width="6" height="${h}"/>`;
        })
        .join("");
    }
  }

  #buildSenses(): void {
    const host = el("tlSensesGrid");

    if (host === null) return;

    host.innerHTML = Array.from(
      { length: CHANNELS },
      (_, c) => `<i data-ch="${c}" title="channel ${c}"></i>`,
    ).join("");
  }

  #paintSenses(live: number | null): void {
    const host = el("tlSensesGrid");

    if (host === null) return;

    const counts = Array.from({ length: CHANNELS }, () => 0);

    for (const feed of this.#feeds) if (feed.channel < CHANNELS) counts[feed.channel] += 1;

    const max = Math.max(1, ...counts);
    const last = this.#feeds[0]?.channel ?? null;

    host.querySelectorAll<HTMLElement>("i").forEach((led, c) => {
      led.style.setProperty("--heat", (counts[c] / max).toFixed(3));
      led.classList.toggle("is-last", c === last);

      if (live !== null && c === live) {
        led.classList.remove("is-fire");
        void led.offsetWidth;
        led.classList.add("is-fire");
      }
    });

    setText("tlSensesS", t("tl.sensesS", { n: fmt(this.#feeds.length) }));
    setText("tlSensesLast", last === null ? "—" : t("tl.channel", { n: String(last) }));
  }

  #paintHeart(): void {
    const blocks = this.#blocks;
    const newest = blocks.at(-1);

    setText("tlBlock", newest === undefined ? "—" : fmt(newest.number));
    setText("tlTxs", newest === undefined ? "—" : fmt(newest.txs));

    const first = blocks[0];

    if (first !== undefined && newest !== undefined && newest.timestamp > first.timestamp) {
      const perMin = ((blocks.length - 1) / (newest.timestamp - first.timestamp)) * 60;

      setText("tlRate", perMin.toFixed(0));
    }

    const svg = el("tlHeartBars");

    if (svg !== null && blocks.length > 0) {
      const max = Math.max(1, ...blocks.map((b) => b.txs));

      svg.innerHTML = blocks
        .map((b, i) => {
          const h = Math.max(1.5, (b.txs / max) * 26);

          return `<rect x="${i * 6.25}" y="${28 - h}" width="4.6" height="${h}"/>`;
        })
        .join("");
    }
  }
}
